import type { HelpAnchorId } from '@myrewards/contracts';
import { useId, type ReactElement, type ReactNode, type Ref } from 'react';
import { CardHelp, type CardHelpContent } from './CardHelp';
import { cx } from './cx';
import { HelpShownTag, useHelpAnchor } from './HelpShown';
import s from './Card.module.css';

export interface CardProps {
  /** The card heading (sentence case; Oswald uppercases it). */
  title: string;
  /** What the "?" is about, when the heading carries more than the name (a count, a state). */
  helpTopic?: string;
  /** Required: every card heading has a "?" with what it shows and what you can do. */
  help: CardHelpContent;
  /** Heading level in the page outline. Default 2. */
  headingLevel?: 2 | 3;
  /** Visual size of the heading: lg 24 px (default), md 22 px, sm 20 px. */
  titleSize?: 'lg' | 'md' | 'sm';
  /** Small element after the heading, such as a count pill. */
  badge?: ReactNode;
  /** Right side of the heading row: a short note or one or two buttons. */
  aside?: ReactNode;
  /** A 4 px colour bar across the top (gold for celebration and Waiting on you). */
  accent?: 'gold' | 'cyan';
  /** `not-set` greys the card (configuration missing); `dark` is the navy contact card. */
  tone?: 'default' | 'not-set' | 'dark';
  /** Decorative photo across the top (alt text is required when it carries meaning). */
  media?: { src: string; alt: string };
  /** Tighter padding (22 × 24) for side cards and KPI tiles. */
  compact?: boolean;
  /**
   * A navy band across the top: the heading, its "?" and this content sit on navy, and the rest
   * of the card stays white. For a card about one person or one choice, as the boards draw the
   * person you picked (SYS6) and "What can this person do" (SYS2). Fields in the band read white.
   */
  band?: ReactNode;
  id?: string;
  /**
   * The heading, for a page that moves focus to this card (a "Jump to" link, a step finished): it
   * becomes focusable by script only.
   */
  headingRef?: Ref<HTMLHeadingElement>;
  /** What Help's "Show me" calls this card (APP-090), from the server's registry. */
  helpAnchor?: HelpAnchorId;
  className?: string;
  children?: ReactNode;
}

/** The white card used for every web part. Its heading always has a card help button. */
export function Card({
  title,
  helpTopic,
  help,
  headingLevel = 2,
  titleSize = 'lg',
  badge,
  aside,
  accent,
  tone = 'default',
  media,
  compact = false,
  band,
  id,
  headingRef,
  helpAnchor,
  className,
  children,
}: CardProps): ReactElement {
  const headingId = useId();
  const anchor = useHelpAnchor(helpAnchor);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const banded = band !== undefined;
  const head = (
    <div className={s.head}>
      <div className={s.titleRow}>
        <Heading
          id={headingId}
          className={s.title}
          {...(headingRef !== undefined && { ref: headingRef, tabIndex: -1 })}
        >
          {title}
        </Heading>
        {badge}
        <CardHelp
          topic={helpTopic ?? title}
          shows={help.shows}
          canDo={help.canDo}
          onDark={tone === 'dark' || banded}
        />
        <HelpShownTag shown={anchor.shown} />
      </div>
      {aside !== undefined && <div className={s.aside}>{aside}</div>}
    </div>
  );
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      {...anchor.props}
      className={cx(
        s.card,
        accent === 'gold' && s.accentGold,
        accent === 'cyan' && s.accentCyan,
        tone === 'not-set' && s.notSet,
        tone === 'dark' && s.dark,
        titleSize === 'md' && s.titleMd,
        titleSize === 'sm' && s.titleSm,
        compact && s.compact,
        banded && s.banded,
        className,
      )}
    >
      {media && <img className={s.media} src={media.src} alt={media.alt} />}
      <div className={s.body}>
        {banded ? (
          <div className={s.band}>
            {head}
            {band}
          </div>
        ) : (
          head
        )}
        {children}
      </div>
    </section>
  );
}
